/**
 * CetusEd NextGen — the data-stack readiness hook.
 *
 * A data route calls this on mount to lazy-initialize the POST-AUTH data stack
 * (keyring + encrypted storage + state + RBAC + lanes + audit; see
 * ../../boot/data-stack). The login page and the app shell NEVER call it — they
 * do not touch student data.
 *
 * ⛔2 FAIL CLOSED / ⛔4 NO FABRICATED SUCCESS: `ready` is set ONLY when
 * ensureDataStack() actually resolves. Today it rejects (the storage gateway
 * refuses), so this resolves to `unavailable` carrying the REAL refusal message
 * — never a fabricated "ready" that would let a screen render student data over
 * a stack that stored none.
 */
import { useEffect, useState } from 'react'
import { ensureDataStack } from '../../boot/data-stack'

export type DataStackStatus = 'initializing' | 'ready' | 'unavailable'

export interface DataStackState {
  status: DataStackStatus
  /** The real refusal/error message when `unavailable`; null otherwise. */
  error: string | null
}

export function useDataStack(): DataStackState {
  const [state, setState] = useState<DataStackState>({ status: 'initializing', error: null })

  useEffect(() => {
    let active = true
    ensureDataStack().then(
      () => {
        if (active) setState({ status: 'ready', error: null })
      },
      (err: unknown) => {
        if (!active) return
        const message =
          err instanceof Error ? err.message : 'The secure data store is not available.'
        setState({ status: 'unavailable', error: message })
      },
    )
    return () => {
      active = false
    }
  }, [])

  return state
}
